/*
 * Autofill guard for password-manager browser extensions (LastPass etc.).
 *
 * Inputs opted out of autofill carry data-lpignore="true". LastPass ignores
 * that attribute when it autofills at page load: its content script writes
 * input.value directly, sometimes dispatching synthetic input/change/keyup
 * events and sometimes dispatching nothing at all, so readonly and
 * autocomplete="off" cannot stop it. On pages that validate the username
 * live, the validation handler clears the field, LastPass fills it again,
 * and the error notification loops forever.
 *
 * Marked inputs also carry the opt-out hints the other managers honor
 * (data-1p-ignore for 1Password, data-bwignore for Bitwarden,
 * data-form-type="other" for Dashlane, data-protonpass-ignore for Proton
 * Pass), so those never offer a fill that this guard would then revert,
 * which talents see as the field clearing itself. Keep the full set on any
 * new marked input; data-lpignore alone is what this guard keys on.
 *
 * Two defenses, both scoped to marked inputs only (the login page keeps
 * working with password managers because its inputs are not marked):
 *
 * 1. Event guard. Extensions cannot dispatch trusted DOM events
 *    (event.isTrusted is false for anything not produced by a real user
 *    gesture), so every untrusted value event is swallowed in the capture
 *    phase before any page handler sees it, and the last legitimate value
 *    is restored.
 *
 * 2. Watchdog poll. Catches fills that dispatch no events. Legitimate
 *    programmatic writes are tracked by patching jQuery.fn.val (all page
 *    scripts populate these fields through jQuery) and
 *    HTMLFormElement.prototype.reset (jQuery's trigger("reset") calls the
 *    native method, which silently restores defaults). Any other silent
 *    value change on an unfocused marked input is reverted.
 */
(function () {
    'use strict';

    var SELECTOR = 'input[data-lpignore="true"]';
    var EVENTS = ['input', 'change', 'keydown', 'keypress', 'keyup', 'paste'];
    var POLL_MS = 300;
    var lastLegit = new WeakMap();

    function isGuarded(el) {
        return !!el && el.nodeType === 1 && typeof el.matches === 'function' && el.matches(SELECTOR);
    }

    function remember(el) {
        lastLegit.set(el, el.value);
    }

    function expected(el) {
        return lastLegit.has(el) ? lastLegit.get(el) : el.defaultValue;
    }

    EVENTS.forEach(function (type) {
        document.addEventListener(type, function (event) {
            var el = event.target;
            if (!isGuarded(el)) {
                return;
            }

            if (event.isTrusted) {
                remember(el);
                return;
            }

            event.stopImmediatePropagation();
            event.preventDefault();

            if ((type === 'input' || type === 'change') && el.value !== expected(el)) {
                el.value = expected(el);
            }
        }, true);
    });

    function patchJQueryVal() {
        var $ = window.jQuery;
        if (!$ || !$.fn || typeof $.fn.val !== 'function' || $.fn.val.__autofillGuard) {
            return !!($ && $.fn && $.fn.val && $.fn.val.__autofillGuard);
        }
        var origVal = $.fn.val;
        $.fn.val = function () {
            var result = origVal.apply(this, arguments);
            if (arguments.length) {
                for (var i = 0; i < this.length; i++) {
                    if (isGuarded(this[i])) {
                        remember(this[i]);
                    }
                }
            }
            return result;
        };
        $.fn.val.__autofillGuard = true;
        return true;
    }

    if (!patchJQueryVal()) {
        document.addEventListener('DOMContentLoaded', patchJQueryVal);
    }

    var origReset = HTMLFormElement.prototype.reset;
    HTMLFormElement.prototype.reset = function () {
        var result = origReset.apply(this, arguments);
        var inputs = this.querySelectorAll(SELECTOR);
        for (var i = 0; i < inputs.length; i++) {
            remember(inputs[i]);
        }
        return result;
    };

    setInterval(function () {
        var inputs = document.querySelectorAll(SELECTOR);
        for (var i = 0; i < inputs.length; i++) {
            var el = inputs[i];
            if (el === document.activeElement) {
                continue;
            }
            var want = expected(el);
            if (el.value !== want) {
                el.value = want;
            }
        }
    }, POLL_MS);
})();
